/**
 * Error Notification Component
 * Toast notifications for displaying errors with auto-dismiss
 */

import { useEffect, useState } from "react";
import {
  globalErrorHandler,
  AppError,
  ErrorCategory,
} from "../lib/errorHandler";

interface Notification extends AppError {
  id: string;
}

export default function ErrorNotification() {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    // Subscribe to global errors
    const unsubscribe = globalErrorHandler.subscribe((error) => {
      // Only show non-critical errors as notifications
      // Critical errors should be handled by error boundaries
      if (error.category !== ErrorCategory.UNKNOWN) {
        const notification: Notification = {
          ...error,
          id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        };

        setNotifications((prev) => [...prev, notification]);

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
          setNotifications((prev) =>
            prev.filter((n) => n.id !== notification.id),
          );
        }, 5000);
      }
    });

    return unsubscribe;
  }, []);

  const dismiss = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {notifications.map((notification) => (
        <div
          key={notification.id}
          className="glass-card p-4 shadow-xl border border-red-500/20 animate-slide-in-right"
        >
          <div className="flex items-start gap-3">
            {/* Icon */}
            <div className="flex-shrink-0">
              {notification.category === ErrorCategory.NETWORK ? (
                <svg
                  className="w-6 h-6 text-orange-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3"
                  />
                </svg>
              ) : notification.category === ErrorCategory.AUTH ? (
                <svg
                  className="w-6 h-6 text-yellow-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">
                {notification.category === ErrorCategory.NETWORK
                  ? "Connection Issue"
                  : notification.category === ErrorCategory.AUTH
                  ? "Authentication Required"
                  : "Error"}
              </p>
              <p className="text-sm text-muted-slate mt-1">
                {notification.userFriendlyMessage}
              </p>
            </div>

            {/* Dismiss button */}
            <button
              onClick={() => dismiss(notification.id)}
              className="flex-shrink-0 text-muted-slate hover:text-foreground transition-colors"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
